import React, { useMemo, useState } from 'react';

export type BankLandingKey = 'franki' | 'skd' | 'wibor';

type Props = {
  locale?: 'pl' | 'en' | 'es';
  slug: BankLandingKey;
};

type FaqItem = { q: string; a: string };

const SECTION_TIGHT: React.CSSProperties = { paddingTop: 18, paddingBottom: 18 };

const CostsTabs: React.FC<{ items: { title: string; text: string }[] }> = ({ items }) => {
  const [active, setActive] = useState(0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, marginBottom: 16 }}>
        {items.map((it, idx) => {
          const isActive = idx === active;
          return (
            <button
              key={it.title}
              type="button"
              onClick={() => setActive(idx)}
              className="chip"
              style={{
                cursor: 'pointer',
                background: '#fff',
                color: '#111',
                borderRadius: 999,
                padding: '10px 14px',
                border: isActive ? '2px solid #0C3831' : '1px solid rgba(0,0,0,0.18)',
                opacity: 1,
              }}
              aria-current={isActive ? 'true' : undefined}
            >
              {it.title}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 0, padding: 0 }}>
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>{items[active]?.title}</h3>
        <p className="muted" style={{ margin: 0 }}>
          {items[active]?.text}
        </p>
      </div>
    </div>
  );
};

export const SporyBankiLanding: React.FC<Props> = ({ locale = 'pl', slug }) => {
  const labels =
    locale === 'en'
      ? {
          help: 'How do we help?',
          costs: 'Service options and fees',
          faq: 'FAQ',
          contactInline: 'Contact us',
          fallbackHelp: 'We will add the details of the process at the next stage.',
          fallbackFaq: 'We will add the FAQ at the next stage of building this landing page.',
          costsFallback: 'Contact us via the form — we will come back with a short proposal of scope and fees.',
          blogPrefix: 'Read on the blog: ',
        }
      : locale === 'es'
      ? {
          help: '¿Cómo ayudamos?',
          costs: 'Opciones de servicio y costes',
          faq: 'FAQ',
          contactInline: 'Contacta con nosotros',
          fallbackHelp: 'Añadiremos los detalles del proceso en la siguiente etapa.',
          fallbackFaq: 'Añadiremos el FAQ en la siguiente etapa de creación de esta landing.',
          costsFallback: 'Contacta a través del formulario — volveremos con una propuesta breve de alcance y costes.',
          blogPrefix: 'Lee en el blog: ',
        }
      : {
          help: 'Jak pomagamy?',
          costs: 'Warianty pomocy i koszty',
          faq: 'FAQ',
          contactInline: 'Skontaktuj się z nami',
          fallbackHelp: 'Opis kroków dodamy za chwilę.',
          fallbackFaq: 'FAQ dodamy na etapie tworzenia tego landingu.',
          costsFallback: 'Skontaktuj się przez formularz — wrócimy z krótką propozycją zakresu i kosztów.',
          blogPrefix: 'Czytaj na blogu: ',
        };
  const page = useMemo(() => {
    const L = locale;

    const labels =
      L === 'en'
        ? {
            help: 'How do we help?',
            costs: 'Service options and fees',
            faq: 'FAQ',
            contactInline: 'Contact us',
            fallbackHelp: 'We will add the details of the process at the next stage.',
            fallbackFaq: 'We will add the FAQ at the next stage of building this landing page.',
            costsFallback:
              'Contact us via the form — we will come back with a short proposal of scope and fees.',
            blogPrefix: 'Read on the blog: ',
          }
        : L === 'es'
        ? {
            help: '¿Cómo ayudamos?',
            costs: 'Opciones de servicio y costes',
            faq: 'FAQ',
            contactInline: 'Contacta con nosotros',
            fallbackHelp: 'Añadiremos los detalles del proceso en la siguiente etapa.',
            fallbackFaq: 'Añadiremos el FAQ en la siguiente etapa de creación de esta landing.',
            costsFallback:
              'Contacta a través del formulario — volveremos con una propuesta breve de alcance y costes.',
            blogPrefix: 'Lee en el blog: ',
          }
        : {
            help: 'Jak pomagamy?',
            costs: 'Warianty pomocy i koszty',
            faq: 'FAQ',
            contactInline: 'Skontaktuj się z nami',
            fallbackHelp: 'Opis kroków dodamy za chwilę.',
            fallbackFaq: 'FAQ dodamy na etapie tworzenia tego landingu.',
            costsFallback:
              'Skontaktuj się przez formularz — wrócimy z krótką propozycją zakresu i kosztów.',
            blogPrefix: 'Czytaj na blogu: ',
          };

    const PL = {
      franki: {
        title: 'Kredyty "frankowe"',
        lead:
          'Kredyty hipoteczne, waloryzowane do walut obcych (głównie CHF), potocznie zwane "frankowymi", od lat budzą duże emocje, zarówno na salach rozpraw, jak i poza nimi. Umowy obejmujące ww. kredyty zawierają klauzule przeliczeniowe, które są uznawane za abuzywne. Kredytodawcy nie informują kredytobiorców o tym, jak działa mechanizm przeliczeniowy z ww. klauzul, na którego wpływ, ma wyłącznie kredytodawca. W związku z powyższym zapadają wyroki ustalające nieważność ww. umów. Dzięki tym wyrokom, konsumenci mogą odzyskać nienależnie uiszczone świadczenia lub wykreślić hipotekę. Banki, chcąc zminimalizować skalę strat w związku z przegraną w sądzie, proponują kredytobiorcom ugody, na różnych etapach postępowania.',
        articleHref: '/blog/kredyty-frankowe-sytuacja-2026.html',
        articleLabel: 'Kredyty frankowe — sytuacja w 2026 r.',
        help: [
          {
            title: 'Analiza dokumentów',
            text:
              'Sprawdzamy istotne dokumenty dotyczące kredytu, w tym przede wzystkim umowę. Dostajesz od nas informację, czy zawarta przez Ciebie umowa jest ważna, a jeśli nie, to jakie roszczenia Tobie przysługują i o jakiej wartości.',
          },
          {
            title: 'Określenie strategii działania',
            text:
              'Razem z Tobą wybieramy najlepszą strategię działania, tak byś czuł się bezpiecznie w sporze z kredytodawcą.',
          },
          {
            title: 'Postępowanie przedsądowe lub ugodowe',
            text:
              'Przygotujemy dla Ciebie wezwanie do zapłaty (poprzedzające złożenie pozwu) lub pomożemy w całej korespondencji, negocjacjach ugodowych oraz przy zawarciu ugody z kredytodawcą (ugoda może być zawarta również po złożeniu pozwu).',
          },
          {
            title: 'Postępowanie sądowe',
            text:
              'Jeśli kredytodawca odmawia uwzględnienia wezwania do zapłaty albo nie doszło do zawarcia ugody, przygotujemy pozew i zastąpimy Ciebie przed sądem, aż do prawomocnego rozstrzygnięcia sprawy.',
          },
        ],
        costs: [
          { title: 'Analiza dokumentów', text: '0 zł' },
          { title: 'Określenie strategii działania', text: '0 zł' },
          { title: 'Postępowanie przedsądowe', text: 'od 1 000 zł brutto' },
          {
            title: 'Postępowanie sądowe',
            text:
              'minimalne koszty: 6 000 zł brutto na start + premia za sukces w wysokości 6%  od kwoty realnie uzyskanej korzyści przez kredytobiorcę (najczęściej od nadpłaty ponad kapitał kredytu, ale korzyścią jest również obniżenie zobowiązania wobec banku) + koszty sądowe + wydatki kancelarii związane z działaniem przed sądem',
          },
        ],
        faq: [
          {
            q: 'Czy w sprawie frankowej zawsze trzeba iść do sądu?',
            a:
              'Nie zawsze. Banki coraz częściej proponują zawarcie ugody jeszcze przed złożeniem pozwu. Od klienta zależy, czy przyjmie warunki takiej ugody, czy zdecyduje się jednak iść do sądu po wyrok, który najprawdopodobnie będzie bardziej korzystny niż ugoda (ale uzyskanie prawomocnego wyroku zajmuje znacznie więcej czasu (2-3 lata), niż zawarcie ugody).',
          },
          {
            q: 'Jakie dokumenty są potrzebne do bezpłatnej analizy?',
            a:
              'Najczęściej: umowa kredytu, regulamin/OWU, aneksy, harmonogramy, historia spłat (z banku) i korespondencja z bankiem. Jeśli czegoś brakuje — wskażemy, jak to szybko zdobyć.',
          },
          {
            q: 'Ile trwa sprawa frankowa?',
            a:
              'Przeciętne 2-3 lata do uzyskania prawomocnego wyroku. W praktyce, to zależy od sądu (w Warszawie czas trwania postępowania może być wydłużony).',
          },
          {
            q: 'Co jeśli bank proponuje ugodę — skąd mam wiedzieć, czy jest „dobra”?',
            a:
              'Należy dokładnie zapoznać się ze wszystkimi postanowieniami ugody, w tym w szczególności z postanowieniami determinującymi rzeczywistą wysokość środków, które uzyska konsument w wyniku zawarcia ugody, postanowień dotyczących statusu umowy kredytu (czy strony uznają ją za ważną), zrzeczenia się roszczeń przez strony ponad świadczenia przyznane mocą ugody. Korzyści ekonomiczne konsumenta wynikające z ugody, należy zestawić z ww. korzyściami, które może on uzyskać na drodze sądowej (przy uwzględnieniu kosztów, czasu, ryzyka). Dopiero wtedy można podjąć racjonalną decyzję. Zachęcamy do skorzystania z naszej pomocy.',
          },
          {
            q: 'Co jeśli bank twierdzi, że „wiedziałem o ryzyku” albo że to ja chciałem CHF?',
            a:
              'To typowa linia argumentcyjna banku. W praktyce sądy stoją na stanowisku, że nawet podpisanie oświadczenia o świadomości istnienia ryzyka kursowego nie jest dowodem, że konsument wiedział, jak będą przeliczane waluty. A kredyt waloryzowany w CHF to faktycznie jedyny kredyt, jaki byli w stanie wziać kredytobiorcy, nawet jeśli na papierze kredytobiorcy mieli alternatywy- i sądy o tym wiedzą.',
          },
          {
            q: 'Czy muszę przyjeżdżać do Warszawy?',
            a:
              'Nie. Analizę dokumentów, określenie strategii, postępowanie przedsądowe oraz sądowe (jeżeli sąd wyznaczy rozprawę w trybie zdalnym) możemy zrobić na odległość. Jeśli pojawia się (co jest rzadkością) potrzeba stawiennictwa klienta spoza Warszawy w stolicy — ustalimy to z wyprzedzeniem.',
          },
          {
            q: 'Jak wygląda rozliczenie (ryczałt + success fee) w praktyce?',
            a:
              'Ryczałt umożlwia nam rozpoczęcie pracy nad Twoją sprawą, dlatego jest płatny z góry, może być w uzgodnionych ratach. Natomiast premia za sukces to nagroda (procent od nadpłaty ponad kapitał- nadpłatę ponad kapitał kredytobiorca powinien uzyskać w każdej sprawie, dotyczącej nieważnych umów o kredyt hipoteczny, waloryzowanych do walut obcych) za korzystne rozstrzygnięcie sprawy.',
          },
        ],
      },

      skd: {
        title: 'Sankcja kredytu darmowego (SKD)',
        lead:
          'Umowa kredytu zawierana na cele konsumenckie powinna odpowiadać wymogom określonym w ustawie z dnia 12 maja 2011 r. o kredycie konsumenckim, Dz. U. 2011 Nr 126 poz. 715 ze zm. Jeżeli ww. umowa kredytu nie odpowiada ww. wymogom, to zastosowanie znajduje sankcja kredytu darmowego (SKD). Sankcja ta, polega na tym, że kredytobiorca płaci tylko kapitał kredytu, bez odsetek i pozostałych kosztów kredytu. Żeby skorzystać z ww. sankcji, kredytobiorca musi złożyć kredytodawcy oświadczenie o skorzystaniu z sankcji kredytu darmowego, a w razie odmowy uwzględnienia tego oświadczenia, dochodzić roszczeń z nim związanych przed sądem.',
        articleHref: '',
        articleLabel: '',
        help: [
          {
            title: 'Analiza dokumentów',
            text:
              'Sprawdzamy istotne dokumenty dotyczące kredytu, w tym przede wzystkim umowę. Dostajesz od nas informację, czy w Twoim przypadku SKD znajduje zastosowanie oraz jakie są Twoje korzyści wynikające z SKD.',
          },
          {
            title: 'Określenie strategii działania',
            text:
              'Razem z Tobą wybieramy najlepszą strategię działania, tak byś czuł się bezpiecznie w sporze z kredytodawcą.',
          },
          {
            title: 'Postępowanie przedsądowe',
            text:
              'Przygotujemy dla Ciebie oświadczenie o skorzystaniu z sankcji kredytu darmowego, wezwanie do zapłaty, pomożemy w całej korespondencji lub negocjacjach z kredytodawcą.',
          },
          {
            title: 'Postępowanie sądowe',
            text:
              'Jeśli kredytodawca odmawia uznania oświadczenia o skorzystaniu z SKD — przygotujemy pozew, wniosek o zabezpiecznie i zastąpimy Ciebie przed sądem, co najmniej do prawomocnego rozstrzygnięcia sprawy.',
          },
        ],
        costs: [
          { title: 'Analiza dokumentów', text: '0 zł' },
          { title: 'Określenie strategii działania', text: '0 zł' },
          { title: 'Postępowanie przedsądowe', text: 'od 1000 zł brutto' },
          {
            title: 'Postępowanie sądowe',
            text:
              'minimalne koszty: 5 000 zł brutto na start + premia za sukces w wysokości 6% od realnej korzyści kredytobiorcy, czyli kwoty kosztów kredytu, których kredytobiorca nie płaci, w wyniku skorzystania z SKD + koszty sądowe + wydatki kancelarii związane z działaniem przed sądem',
          },
        ],
        faq: [
          {
            q: 'Na czym polega sankcja kredytu darmowego (SKD)?',
            a:
              'Na rozliczeniu kredytu tak, jakby był bez kosztów (w tym odsetek kapitałowych). Jest to możliwe tylko w przypadku niezgodności umowy o kredyt konsumencki z określonymi wymogami, wynikającymi z ww. ustawy o kredycie konsumenckim. Ocenimy to w ramach darmowej analizy.',
          },
          {
            q: 'Jakie dokumenty są potrzebne do bezpłatnej analizy?',
            a:
              'Najczęściej jest to umowa kredytu, regulamin kredytu, ogólne warunki umowy, harmonogram spłat, tabela opłat/prowizji oraz zaświadczenie o historii spłat. Jeśli czegoś brakuje — doradzimy, jak to zdobyć.',
          },
          {
            q: 'Czy da się załatwić SKD bez sądu?',
            a:
              'Zawsze zaczynamy od działań przedsądowych. Jeśli bank odmawia uznania oświadczenia o skorzystaniu z skd, trzeba iść do sądu.',
          },
          {
            q: 'Ile trwa sprawa o SKD?',
            a:
              'To zależy od tego, na którym etapie zakończy się ta sprawa. Jeżeli na etapie przedsądowym, to może się zakończyć nawet w ciągu 3-4 tygodni. Jeżeli na etapie sądowym, to na prawomocne rozstrzygnięcie sporu można czekać ok. 2-3 lata. Obecnie banki nie są skłonne do uznawania za zasadne oświadczeń o skorzystniu z skd, wobec tego sprawy dotyczące skd trafiają do sądów. Zawsze przedstawiamy klientowi realny horyznt czasowy sprawy, ze względu na jej specyfikę.',
          },
        ],
      },

      wibor: {
        title: 'Umowy kredytowe oparte o WIBOR',
        lead:
          'Umowy o kredyt o zmiennym oprocentowaniu, zawierające wskaźnik WIBOR, stanowią obecnie przedmiot intensywnej debaty prawnej. Przeważają argumenty przemawiające za abuzywnością klauzul zmiennego oprocentowania, opartych o ww. wskaźnik. Po wydaniu przez Trybunał Sprawiedliwości Unii Europejskiej orzeczenia w sprawie o sygn. akt: C-471/24, podkreśla się możliwość stwierdzenia abuzywności ww. klauzul, w wypadku braku przejrzystości postanowień umownych definiujących wskaźnik WIBOR oraz braku spełnienia obowiązku informacyjnego przez kredytodawcę wobec kredytobiorcy. Ocena skutków prawnych ww. stwierdzenia abuzywności należy, zgodnie z ww. wyrokiem, do sądów krajowych, ale dominują głosy o nieważności takiej umowy, w mniejszości pozostają poglądy o konieczności zastąpienia WIBOR-u stałym oprocentowaniem, alternatywnym wskaźnikiem lub innej korekty dotychczasowych rozliczeń.',
        articleHref: '',
        articleLabel: '',
        help: [
          {
            title: 'Analiza dokumentów',
            text:
              'Sprawdzamy istotne dokumenty dotyczące kredytu, w tym przede wzystkim umowę. Dostajesz od nas informację, jakich roszczeń możesz dochodzić i o jakiej wartości.',
          },
          {
            title: 'Określenie strategii działania',
            text:
              'Razem z Tobą wybieramy najlepszą strategię działania, tak byś czuł się bezpiecznie w sporze z kredytodawcą.',
          },
          {
            title: 'Postępowanie przedsądowe',
            text:
              'Przygotujemy dla Ciebie wezwanie do zapłaty (poprzedzające złożenie pozwu) lub pomożemy w całej korespondencji oraz negocjacjach z kredytodawcą.',
          },
          {
            title: 'Postępowanie sądowe',
            text:
              'Jeśli kredytodawca odmawia uwzględnienia wezwania do zapłaty, przygotujemy pozew i zastąpimy Ciebie przed sądem, aż do prawomocnego rozstrzygnięcia sprawy.',
          },
        ],
        costs: [
          { title: 'Analiza dokumentów', text: '0 zł' },
          { title: 'Określenie strategii działania', text: '0 zł' },
          { title: 'Postępowanie przedsądowe', text: 'Od 2 000 zł brutto' },
          {
            title: 'Postępowanie sądowe',
            text:
              'minimalne koszty: 7 000 zł brutto na start + premia za sukces w wysokości 6% od kwoty realnie uzyskanej korzyści (umowa o pomoc prawną definiuje ww. korzyść) + koszty sądowe + wydatki kancelarii związane z działaniem przed sądem)',
          },
        ],
        faq: [
          {
            q: 'Jakie dokumenty są potrzebne do bezpłatnej analizy?',
            a:
              'Najczęściej: umowa kredytu, regulamin/OWU, aneksy, harmonogramy, historia spłat (z banku) i korespondencja z bankiem. Jeśli czegoś brakuje — wskażemy, jak to szybko zdobyć.',
          },
          {
            q: 'Ile trwa sprawa WIBOR?',
            a:
              'Przeciętne 3-4 lata do uzyskania prawomocnego wyroku. W praktyce, to zależy od sądu (w Warszawie czas trwania postępowania może być wydłużony).',
          },
          {
            q: 'Czy mogę liczyć na wsparcie, jeśli bank proponuje ugodę lub „specjalne warunki”?',
            a:
              'Tak. Przeanalizujemy propozycję banku i wskażemy, czy warto na nią przystać.',
          },
          {
            q: 'Czy muszę przyjeżdżać osobiście do Warszawy?',
            a:
              'Nie. Analizę dokumentów, określenie strategii, postępowanie przedsądowe oraz sądowe (jeżeli sąd wyznaczy rozprawę w trybie zdalnym) możemy zrobić na odległość. Jeśli pojawia się (co jest rzadkością) potrzeba stawiennictwa klienta spoza Warszawy w stolicy — ustalimy to z wyprzedzeniem.',
          },
        ],
      },
    } as const;

    const EN = {
      franki: {
        title: 'CHF-linked mortgages ("franc loans")',
        lead:
          'Mortgages indexed to foreign currencies (mainly CHF), commonly referred to as “CHF/franc loans”, have been generating strong emotions for years — both in courtrooms and beyond. These agreements often include conversion clauses that are considered unfair (abusive). Lenders frequently failed to explain how the conversion mechanism worked, even though it was controlled by the lender. As a result, courts issue judgments declaring such agreements invalid. Thanks to such judgments, consumers may recover undue payments or remove the mortgage entry. To minimise losses, banks increasingly offer settlements at various stages of the dispute.',
        articleHref: PL.franki.articleHref,
        articleLabel: 'CHF loans — situation in 2026',
        help: [
          {
            title: 'Document review',
            text:
              'We review the key loan documents, in particular the agreement. You receive an assessment whether your agreement is valid and, if not, which claims you may pursue and their estimated value.',
          },
          {
            title: 'Strategy selection',
            text:
              'Together we choose the best strategy so you feel secure in a dispute with the lender.',
          },
          {
            title: 'Pre-litigation stage or settlement',
            text:
              'We prepare a demand letter (before filing a claim) or support you in correspondence, settlement negotiations and the settlement itself (a settlement can also be concluded after filing).',
          },
          {
            title: 'Court proceedings',
            text:
              'If the lender refuses to comply with the demand letter or no settlement is reached, we prepare the claim and represent you in court until a final judgment.',
          },
        ],
        costs: [
          { title: 'Document review', text: 'PLN 0' },
          { title: 'Strategy selection', text: 'PLN 0' },
          { title: 'Pre-litigation stage', text: 'from PLN 1,000 gross' },
          {
            title: 'Court proceedings',
            text:
              'minimum fees: PLN 6,000 gross upfront + success fee of 6% of the borrower’s real financial benefit (most often the overpayment above principal; the benefit may also be the reduction of the bank debt) + court fees + the firm’s out-of-pocket expenses related to court proceedings',
          },
        ],
        faq: [
          {
            q: 'Do CHF cases always require going to court?',
            a:
              'Not always. Banks increasingly offer settlements even before a lawsuit is filed. It is up to the client to accept the settlement terms or pursue a court judgment, which is likely to be more favourable than a settlement (but takes significantly longer — typically 2–3 years).',
          },
          {
            q: 'Which documents are needed for the free review?',
            a:
              'Most often: the loan agreement, terms & conditions (OWU), annexes, repayment schedules, repayment history (from the bank) and correspondence with the bank. If anything is missing — we will advise how to obtain it quickly.',
          },
          {
            q: 'How long does a CHF case take?',
            a:
              'Typically 2–3 years to obtain a final judgment. In practice it depends on the court (in Warsaw the duration may be longer).',
          },
          {
            q: 'If the bank offers a settlement, how do I know whether it is “good”?',
            a:
              'You should review all settlement terms carefully — in particular the provisions determining the actual amount the consumer will receive, the status of the loan agreement (whether the parties treat it as valid), and any waivers of claims beyond the settlement benefits. The economic effect should be compared with what you could achieve in court (taking into account costs, time and risk). Only then can you make a rational decision. We encourage you to use our support.',
          },
          {
            q: 'What if the bank claims that “I knew the risk” or that I chose CHF myself?',
            a:
              'This is a common line of argument. In practice, courts often hold that even signing a risk-awareness statement does not prove the consumer understood the currency conversion mechanism. Also, many borrowers could realistically only obtain a CHF-linked loan, even if alternatives existed on paper — and courts are aware of this context.',
          },
          {
            q: 'Do I have to travel to Warsaw?',
            a:
              'No. Document review, strategy selection, pre-litigation steps and court proceedings (if the court schedules a remote hearing) can be handled remotely. If a personal appearance is required (rare), we will agree it in advance.',
          },
          {
            q: 'How does the (flat fee + success fee) model work in practice?',
            a:
              'The flat fee allows us to start working and is paid upfront (possibly in agreed instalments). The success fee is a reward (a percentage of the overpayment above principal — which borrowers typically recover when the agreement is declared invalid) for a favourable outcome.',
          },
        ],
      },

      skd: {
        title: 'Free-credit sanction (SKD)',
        lead:
          'A consumer loan agreement should meet the requirements of the Polish Consumer Credit Act of 12 May 2011 (Dz.U. 2011 Nr 126 poz. 715 as amended). If the agreement does not meet these requirements, the so-called free-credit sanction (SKD) may apply. In such a case, the borrower pays only the principal, without interest and other credit costs. To use SKD, the borrower must submit a statement to the lender, and if the lender refuses to recognise it, pursue the related claims in court.',
        articleHref: PL.skd.articleHref,
        articleLabel: 'SKD — what you should know',
        help: [
          {
            title: 'Document review',
            text:
              'We review the key credit documents, in particular the agreement. You receive information whether SKD applies in your case and what benefits you may obtain.',
          },
          {
            title: 'Strategy selection',
            text:
              'Together we choose the best strategy so you feel secure in a dispute with the lender.',
          },
          {
            title: 'Pre-litigation stage',
            text:
              'We prepare the SKD statement, a demand letter, and support you in correspondence or negotiations with the lender.',
          },
          {
            title: 'Court proceedings',
            text:
              'If the lender refuses to recognise the SKD statement — we prepare the claim, an application for interim protection and represent you in court at least until a final judgment.',
          },
        ],
        costs: [
          { title: 'Document review', text: 'PLN 0' },
          { title: 'Strategy selection', text: 'PLN 0' },
          { title: 'Pre-litigation stage', text: 'from PLN 1,000 gross' },
          {
            title: 'Court proceedings',
            text:
              'minimum fees: PLN 5,000 gross upfront + success fee of 6% of the borrower’s real benefit, i.e. the credit costs the borrower does not pay thanks to SKD + court fees + the firm’s out-of-pocket expenses related to court proceedings',
          },
        ],
        faq: [
          {
            q: 'What is the free-credit sanction (SKD)?',
            a:
              'It means settling the credit as if it were free of costs (including interest). It is possible only when the consumer credit agreement does not comply with statutory requirements. We assess this within the free review.',
          },
          {
            q: 'Which documents are needed for the free review?',
            a:
              'Most often: the credit agreement, credit regulations/terms, general conditions, repayment schedule, fee/commission table and repayment history certificate. If anything is missing — we will advise how to obtain it.',
          },
          {
            q: 'Can SKD be resolved without going to court?',
            a:
              'We always start with pre-litigation steps. If the bank refuses to recognise the SKD statement, court proceedings are necessary.',
          },
          {
            q: 'How long does an SKD case take?',
            a:
              'It depends on the stage at which the case ends. If resolved pre-litigation, it may finish even within 3–4 weeks. If it goes to court, a final judgment may take about 2–3 years. Currently banks are generally reluctant to recognise SKD statements, so many cases go to court. We always present a realistic timeline based on the specifics.',
          },
        ],
      },

      wibor: {
        title: 'Loan agreements based on WIBOR',
        lead:
          'Variable-rate loan agreements that use the WIBOR benchmark are currently the subject of an intensive legal debate. Many arguments support the view that WIBOR-based variable-interest clauses may be unfair (abusive). After the judgment of the Court of Justice of the European Union in case C-471/24, commentators emphasise that such clauses may be found abusive where the contractual terms defining WIBOR are not transparent and where the lender failed to fulfil information duties. Under that judgment, national courts assess the legal consequences of such abusiveness. Many voices point to the invalidity of the agreement; a minority argues for replacing WIBOR with a fixed rate, an alternative benchmark, or another adjustment of past settlements.',
        articleHref: '',
        articleLabel: '',
        help: [
          {
            title: 'Document review',
            text:
              'We review the key credit documents, in particular the agreement. You receive information which claims you may pursue and their estimated value.',
          },
          {
            title: 'Strategy selection',
            text:
              'Together we choose the best strategy so you feel secure in a dispute with the lender.',
          },
          {
            title: 'Pre-litigation stage',
            text:
              'We prepare a demand letter (before filing a claim) or support you in correspondence and negotiations with the lender.',
          },
          {
            title: 'Court proceedings',
            text:
              'If the lender refuses to comply with the demand letter, we prepare the claim and represent you in court until a final judgment.',
          },
        ],
        costs: [
          { title: 'Document review', text: 'PLN 0' },
          { title: 'Strategy selection', text: 'PLN 0' },
          { title: 'Pre-litigation stage', text: 'from PLN 2,000 gross' },
          {
            title: 'Court proceedings',
            text:
              'minimum fees: PLN 7,000 gross upfront + success fee of 6% of the real benefit (defined in the legal services agreement) + court fees + the firm’s out-of-pocket expenses related to court proceedings',
          },
        ],
        faq: [
          {
            q: 'Which documents are needed for the free review?',
            a:
              'Most often: the loan agreement, terms & conditions (OWU), annexes, repayment schedules, repayment history (from the bank) and correspondence with the bank. If anything is missing — we will advise how to obtain it quickly.',
          },
          {
            q: 'How long does a WIBOR case take?',
            a:
              'Typically 3–4 years to obtain a final judgment. In practice it depends on the court (in Warsaw the duration may be longer).',
          },
          {
            q: 'Can I get support if the bank offers a settlement or “special conditions”?',
            a:
              'Yes. We will analyse the bank’s proposal and indicate whether it is worth accepting.',
          },
          {
            q: 'Do I have to travel to Warsaw in person?',
            a:
              'No. Document review, strategy selection, pre-litigation steps and court proceedings (if the court schedules a remote hearing) can be handled remotely. If a personal appearance is required (rare), we will agree it in advance.',
          },
        ],
      },
    } as const;

    const ES = {
      franki: {
        title: 'Créditos “francos” (CHF)',
        lead:
          'Los créditos hipotecarios indexados a divisas extranjeras (principalmente CHF), conocidos coloquialmente como “créditos francos”, generan grandes emociones desde hace años, tanto en los tribunales como fuera de ellos. Estos contratos suelen contener cláusulas de conversión consideradas abusivas. A menudo el prestamista no explica cómo funciona el mecanismo de conversión, pese a que su control recae exclusivamente en el prestamista. Por ello, se dictan sentencias que declaran la nulidad de dichos contratos. Gracias a estas sentencias, los consumidores pueden recuperar pagos indebidos o cancelar la hipoteca registrada. Para minimizar pérdidas, los bancos proponen acuerdos en distintas fases del procedimiento.',
        articleHref: PL.franki.articleHref,
        articleLabel: 'Créditos CHF — situación en 2026',
        help: [
          {
            title: 'Análisis de documentos',
            text:
              'Revisamos los documentos clave del crédito, en particular el contrato. Recibes información sobre si tu contrato es válido y, si no lo es, qué reclamaciones te corresponden y su valor estimado.',
          },
          {
            title: 'Definición de estrategia',
            text:
              'Junto contigo elegimos la mejor estrategia para que te sientas seguro en la disputa con el prestamista.',
          },
          {
            title: 'Fase previa o acuerdo',
            text:
              'Preparamos requerimiento de pago (previo a la demanda) o ayudamos con la correspondencia, negociaciones y formalización del acuerdo (el acuerdo también puede celebrarse después de presentar demanda).',
          },
          {
            title: 'Procedimiento judicial',
            text:
              'Si el prestamista rechaza el requerimiento o no se llega a un acuerdo, preparamos la demanda y te representamos hasta una resolución firme.',
          },
        ],
        costs: [
          { title: 'Análisis de documentos', text: '0 PLN' },
          { title: 'Definición de estrategia', text: '0 PLN' },
          { title: 'Fase previa', text: 'desde 1.000 PLN brutos' },
          {
            title: 'Procedimiento judicial',
            text:
              'costes mínimos: 6.000 PLN brutos de inicio + prima de éxito del 6% del beneficio real del prestatario (normalmente el exceso pagado sobre el principal; también puede ser la reducción de la deuda) + tasas judiciales + gastos del despacho relacionados con el procedimiento',
          },
        ],
        faq: [
          {
            q: '¿En un caso “franco” siempre hay que ir a juicio?',
            a:
              'No siempre. Los bancos cada vez más ofrecen acuerdos incluso antes de presentar demanda. El cliente decide si acepta el acuerdo o busca una sentencia, que probablemente será más favorable (pero tarda más — normalmente 2–3 años).',
          },
          {
            q: '¿Qué documentos se necesitan para el análisis gratuito?',
            a:
              'Normalmente: contrato, condiciones/OWU, anexos, calendarios de pago, historial de pagos (del banco) y correspondencia. Si falta algo, indicaremos cómo obtenerlo rápidamente.',
          },
          {
            q: '¿Cuánto dura un caso “franco”?',
            a:
              'Normalmente 2–3 años hasta sentencia firme. En la práctica depende del juzgado (en Varsovia puede tardar más).',
          },
          {
            q: 'Si el banco propone un acuerdo, ¿cómo sé si es “bueno”?',
            a:
              'Hay que revisar cuidadosamente todas las cláusulas: el importe real que obtendrá el consumidor, el estatus del contrato (si las partes lo consideran válido) y las renuncias de reclamaciones. Luego se compara con lo que podría lograrse en juicio (costes, tiempo, riesgo). Solo entonces se toma una decisión racional. Te animamos a usar nuestra ayuda.',
          },
          {
            q: '¿Qué pasa si el banco dice que “conocía el riesgo” o que yo elegí CHF?',
            a:
              'Es una línea típica de defensa. En la práctica, los tribunales suelen considerar que incluso una declaración de conocimiento del riesgo no prueba que el consumidor entendiera el mecanismo de conversión. Además, muchos prestatarios solo podían obtener un crédito indexado a CHF, aunque existieran alternativas “en papel”, y los tribunales lo tienen en cuenta.',
          },
          {
            q: '¿Tengo que viajar a Varsovia?',
            a:
              'No. El análisis, la estrategia, la fase previa y el proceso (si la vista es remota) pueden hacerse a distancia. Si se requiere presencia personal (raro), lo acordaremos con antelación.',
          },
          {
            q: '¿Cómo funciona el modelo (tarifa fija + éxito) en la práctica?',
            a:
              'La tarifa fija permite iniciar el trabajo y se paga por adelantado (posiblemente en cuotas). La prima de éxito es una recompensa (porcentaje del exceso pagado sobre el principal, que suele recuperarse cuando el contrato es nulo) por un resultado favorable.',
          },
        ],
      },

      skd: {
        title: 'Sanción de crédito gratuito (SKD)',
        lead:
          'Un contrato de crédito celebrado con fines de consumo debe cumplir los requisitos de la Ley polaca de crédito al consumo de 12 de mayo de 2011 (Dz. U. 2011 Nr 126 poz. 715, con modificaciones). Si el contrato no cumple dichos requisitos, puede aplicarse la sanción de crédito gratuito (SKD). Esta sanción implica que el prestatario paga solo el capital, sin intereses ni otros costes. Para beneficiarse de la sanción, el prestatario debe presentar al prestamista una declaración y, si este la rechaza, reclamar ante el tribunal.',
        articleHref: PL.skd.articleHref,
        articleLabel: 'SKD — lo que conviene saber',
        help: [
          {
            title: 'Análisis de documentos',
            text:
              'Revisamos los documentos clave, especialmente el contrato. Recibes información sobre si SKD aplica y cuáles son tus beneficios.',
          },
          {
            title: 'Definición de estrategia',
            text:
              'Junto contigo elegimos la mejor estrategia para que te sientas seguro en la disputa con el prestamista.',
          },
          {
            title: 'Fase previa',
            text:
              'Preparamos la declaración SKD, el requerimiento de pago y ayudamos en correspondencia o negociaciones con el prestamista.',
          },
          {
            title: 'Procedimiento judicial',
            text:
              'Si el prestamista rechaza la declaración SKD, preparamos la demanda, la solicitud de medidas cautelares y te representamos al menos hasta una resolución firme.',
          },
        ],
        costs: [
          { title: 'Análisis de documentos', text: '0 PLN' },
          { title: 'Definición de estrategia', text: '0 PLN' },
          { title: 'Fase previa', text: 'desde 1.000 PLN brutos' },
          {
            title: 'Procedimiento judicial',
            text:
              'costes mínimos: 5.000 PLN brutos de inicio + prima de éxito del 6% del beneficio real del prestatario (los costes del crédito que deja de pagar gracias a SKD) + tasas judiciales + gastos del despacho relacionados con el procedimiento',
          },
        ],
        faq: [
          {
            q: '¿En qué consiste la sanción de crédito gratuito (SKD)?',
            a:
              'Consiste en liquidar el crédito como si fuera sin costes (incluidos intereses). Solo es posible si el contrato no cumple requisitos legales. Lo evaluamos en el análisis gratuito.',
          },
          {
            q: '¿Qué documentos se necesitan para el análisis gratuito?',
            a:
              'Normalmente: contrato, reglamento/condiciones, condiciones generales, calendario de pagos, tabla de comisiones y certificado del historial de pagos. Si falta algo, te indicaremos cómo obtenerlo.',
          },
          {
            q: '¿Se puede resolver SKD sin juicio?',
            a:
              'Siempre empezamos por actuaciones previas. Si el banco rechaza la declaración SKD, será necesario acudir a los tribunales.',
          },
          {
            q: '¿Cuánto dura un caso SKD?',
            a:
              'Depende de la fase en la que termine. Si se resuelve en fase previa, puede finalizar incluso en 3–4 semanas. Si va a juicio, una resolución firme puede tardar aprox. 2–3 años. Actualmente los bancos suelen rechazar estas declaraciones, por lo que muchos casos llegan a los tribunales. Siempre damos un horizonte temporal realista.',
          },
        ],
      },

      wibor: {
        title: 'Contratos de crédito basados en WIBOR',
        lead:
          'Los contratos de crédito con tipo variable que incluyen el índice WIBOR son actualmente objeto de un intenso debate jurídico. Predominan argumentos a favor de considerar abusivas las cláusulas de interés variable basadas en dicho índice. Tras la sentencia del Tribunal de Justicia de la Unión Europea en el asunto C-471/24, se subraya la posibilidad de declarar abusivas estas cláusulas cuando falte transparencia en los términos que definen WIBOR y cuando el prestamista no haya cumplido con su deber de información. La evaluación de las consecuencias jurídicas corresponde a los tribunales nacionales. Muchos sostienen la nulidad del contrato; una minoría defiende sustituir WIBOR por un tipo fijo, un índice alternativo u otra corrección de liquidaciones.',
        articleHref: '',
        articleLabel: '',
        help: [
          {
            title: 'Análisis de documentos',
            text:
              'Revisamos los documentos clave del crédito, especialmente el contrato. Recibes información sobre qué reclamaciones puedes plantear y su valor estimado.',
          },
          {
            title: 'Definición de estrategia',
            text:
              'Junto contigo elegimos la mejor estrategia para que te sientas seguro en la disputa con el prestamista.',
          },
          {
            title: 'Fase previa',
            text:
              'Preparamos requerimiento de pago (previo a la demanda) o ayudamos con la correspondencia y negociaciones con el prestamista.',
          },
          {
            title: 'Procedimiento judicial',
            text:
              'Si el prestamista rechaza el requerimiento, preparamos la demanda y te representamos hasta una resolución firme.',
          },
        ],
        costs: [
          { title: 'Análisis de documentos', text: '0 PLN' },
          { title: 'Definición de estrategia', text: '0 PLN' },
          { title: 'Fase previa', text: 'desde 2.000 PLN brutos' },
          {
            title: 'Procedimiento judicial',
            text:
              'costes mínimos: 7.000 PLN brutos de inicio + prima de éxito del 6% del beneficio real (definido en el contrato de servicios) + tasas judiciales + gastos del despacho relacionados con el procedimiento',
          },
        ],
        faq: [
          {
            q: '¿Qué documentos se necesitan para el análisis gratuito?',
            a:
              'Normalmente: contrato, condiciones/OWU, anexos, calendarios de pago, historial de pagos y correspondencia. Si falta algo, indicaremos cómo obtenerlo rápidamente.',
          },
          {
            q: '¿Cuánto dura un caso WIBOR?',
            a:
              'Normalmente 3–4 años hasta sentencia firme. En la práctica depende del juzgado (en Varsovia puede tardar más).',
          },
          {
            q: '¿Puedo contar con apoyo si el banco ofrece un acuerdo o “condiciones especiales”?',
            a:
              'Sí. Analizaremos la propuesta del banco e indicaremos si merece la pena aceptarla.',
          },
          {
            q: '¿Tengo que viajar a Varsovia en persona?',
            a:
              'No. El análisis, la estrategia, la fase previa y el juicio (si la vista es remota) pueden hacerse a distancia. Si se requiere presencia personal (raro), lo acordaremos con antelación.',
          },
        ],
      },
    } as const;

    const dict =
      L === 'en' ? EN : L === 'es' ? ES : (PL as unknown as typeof EN);

    return dict[slug] as any;
  }, [locale, slug]);

  return (
    <main>
      <section className="landing">
        <div className="container">
          <h1 className="landing-title">{page.title}</h1>
          <p className="landing-lead">{page.lead}</p>

          {'articleHref' in page && (page as any).articleHref ? (
  <div style={{ marginTop: 10 }}>
    <a href={(page as any).articleHref} className="inline-link">
      {labels.blogPrefix}
      {(page as any).articleLabel}
    </a>
  </div>
) : (
  <div style={{ marginTop: 10 }}>
    <a href="/blog#franki-skd-wibor" className="inline-link">
      {locale === 'en'
        ? 'Visit our blog'
        : locale === 'es'
        ? 'Visita nuestro blog'
        : 'Zajrzyj na nasz blog'}
    </a>
  </div>
)}
        </div>
      </section>

      {/* JAK POMAGAMY */}
      <section style={{ ...SECTION_TIGHT, paddingTop: 0 }}>
        <div className="container">
          <h2 className="section-heading">{labels.help}</h2>

          {'help' in page && (page as any).help?.length ? (
            <div
              className="forwhom-grid"
              style={{
                marginTop: 14,
                gridTemplateColumns: '1fr',
              }}
            >
              {(page as any).help.map((step: any, index: number) => (
                <div
                  key={step.title ?? index}
                  style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'flex-start',
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                  }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 999,
                      background: '#FBF8F3',
                      border: '2px solid #0C3831',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      color: '#111',
                      flex: '0 0 38px',
                      lineHeight: '38px',
                    }}
                    aria-hidden="true"
                  >
                    {index + 1}
                  </div>

                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: 6 }}>{step.title}</h3>
                    <p className="muted" style={{ margin: 0 }}>
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">{labels.fallbackHelp}</p>
          )}
        </div>
      </section>

      {/* KOSZTY */}
      <section style={SECTION_TIGHT}>
        <div className="container">
          <h2 className="section-heading">{labels.costs}</h2>

          {'costs' in page && (page as any).costs?.length ? (
            <CostsTabs items={(page as any).costs} />
          ) : (
            <p className="muted">{labels.costsFallback}</p>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section style={SECTION_TIGHT}>
        <div className="container">
          <h2 className="section-heading">{labels.faq}</h2>

          {'faq' in page && (page as any).faq?.length ? (
            <div className="faq">
              {(page as any).faq.map((f: FaqItem) => (
                <details key={f.q}>
                  <summary>{f.q}</summary>
                  <div className="muted" style={{ marginTop: 8 }}>
                    {f.a}
                  </div>
                </details>
              ))}
            </div>
          ) : (
            <p className="muted">{labels.fallbackFaq}</p>
          )}
        </div>
      </section>

      {/* CONTACT LINK */}
      <section style={SECTION_TIGHT}>
        <div className="container">
          <a href="/#kontakt" className="inline-link">
            {labels.contactInline}
          </a>
        </div>
      </section>
    </main>
  );
};